import { z } from 'zod';

import { projectUrlSchema } from '../vendors/schemas';

/**
 * Form schemas for the admin panel.
 *
 * - Wire schemas from `@keboola/api-client/developerPortal` are reused where they already state the rule.
 */

const requiredString = z.string().min(1, 'Required');

/** The reason is shown to the vendor, so it must not be empty. */
export const rejectAppFormSchema = z.object({ reason: requiredString });

/**
 * The two ids have different character sets on purpose.
 *
 * - `tmpId` is generated by apps-api and may contain `_` and `.`.
 * - `newId` is the permanent, human-chosen id.
 */
export const approveVendorFormSchema = z.object({
  tmpId: requiredString
    .max(32, 'is too long (maximum is 32 characters)')
    .regex(
      /^[a-z0-9._-]+$/,
      'can only contain lowercase alphanumeric characters, dash, underscore and dot.',
    ),
  newId: requiredString
    .max(32, 'is too long (maximum is 32 characters)')
    .regex(/^[a-z0-9-]+$/, 'can only contain lowercase alphanumeric characters and dash.'),
});

/**
 * Admin vendor form: one schema per mode, each validating only what its form renders.
 *
 * - `id` exists only when creating — changing it later would orphan the vendor's components.
 * - `projectUrl` exists only when editing — `POST /admin/vendors` takes no such field.
 */
const adminVendorCommonFields = {
  name: requiredString.max(128, 'is too long (maximum is 128 characters)'),
  email: requiredString.pipe(z.email('Enter a valid email address')),
  address: requiredString,
};

export const adminVendorCreateFormSchema = z.object({
  ...adminVendorCommonFields,
  id: requiredString
    .max(32, 'is too long (maximum is 32 characters)')
    .regex(/^[a-z0-9-]+$/, 'can only contain lowercase alphanumeric characters and dash.'),
});

export const adminVendorEditFormSchema = z.object({
  ...adminVendorCommonFields,
  // Pins the protocol, which bare `z.url()` does not.
  projectUrl: z.union([z.literal(''), projectUrlSchema]),
});

export const manageUserVendorsFormSchema = z.object({
  action: z.union([z.literal('add'), z.literal('remove')]),
  vendor: requiredString,
});

/** Derives a vendor id from a name — the default `id` when creating. */
export const suggestVendorId = (name: string): string =>
  name
    .replaceAll(/[^a-zA-Z0-9]+/g, '-')
    .toLowerCase()
    .replace(/^-+|-+$/g, '');
